/**
 * Identity tokens for geometry objects.
 *
 * Cache keys that depend on "which geometry is this area pointing at" need a
 * short, stable stand-in for the geometry's identity — serializing the
 * coordinates would cost more than the computation being cached. A `WeakMap`
 * from the geometry object to an integer gives exactly that, and lets the
 * geometry be garbage collected as usual.
 *
 * Keying on identity assumes geometry objects are replaced, never mutated in
 * place. They are, everywhere this is used: geometry fetches write a whole new
 * object. A geometry mutated in place would keep its token, and the cache would
 * keep serving the answer for the old shape.
 */

/** A private geometry-identity table. Create one per cache; see the factory. */
export interface GeometryTokenTable {
  /**
   * The token for a geometry object, minted on first sight.
   * @param {GeoJSON.Geometry} geometry - The geometry
   * @return {number} A token stable for this object's lifetime
   */
  token(geometry: GeoJSON.Geometry): number;
  /** Forgets every geometry and restarts the counter. Primarily for tests. */
  reset(): void;
}

/**
 * Creates an independent geometry-identity table.
 *
 * Each caller gets its OWN instance, deliberately. Tokens are only meaningful
 * relative to the cache key they are embedded in, and `reset()` restarts the
 * counter — so a table shared between two caches lets one cache's reset
 * re-issue token 1 while the other cache still holds a live key containing
 * token 1. That is a stale cache *hit*, i.e. wrong answers, not just coupling.
 * A `WeakMap` and an integer are cheap; sharing the code but not the instance
 * is the whole point of this factory.
 * @return {GeometryTokenTable} A fresh, empty table
 */
export function createGeometryTokenTable(): GeometryTokenTable {
  let tokens = new WeakMap<object, number>();
  let nextToken = 0;

  return {
    token(geometry: GeoJSON.Geometry): number {
      const existing = tokens.get(geometry);
      if (existing !== undefined) return existing;

      nextToken += 1;
      tokens.set(geometry, nextToken);
      return nextToken;
    },
    reset(): void {
      // Replaced rather than cleared: a geometry object carried over from a
      // previous test would otherwise keep its token and make key assertions
      // order-dependent.
      tokens = new WeakMap<object, number>();
      nextToken = 0;
    },
  };
}
